/**
 * Direct-to-S3 uploads (Jun 2026 perf).
 *
 * Files go browser → S3 via presigned PUT URLs instead of transiting the app
 * server, roughly halving upload time and freeing backend workers. Every
 * caller keeps the legacy proxied multipart upload as a fallback, so if the
 * bucket CORS isn't configured (or presign fails) uploads still succeed.
 */
import axios from 'axios';

/** Small square-ish JPEG for grid views (~10-30 KB). Returns File or null. */
export async function makeThumbnail(file, maxDim = 480, quality = 0.72) {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('thumbnail decode failed'));
      i.src = url;
    });
    URL.revokeObjectURL(url);
    let { naturalWidth: w, naturalHeight: h } = img;
    if (!w || !h) return null;
    if (w > maxDim || h > maxDim) {
      const s = maxDim / Math.max(w, h);
      w = Math.round(w * s);
      h = Math.round(h * s);
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', quality));
    return blob ? new File([blob], 'thumb.jpg', { type: 'image/jpeg' }) : null;
  } catch {
    return null;
  }
}

const putToS3 = (url, file, contentType, onProgress) =>
  axios.put(url, file, {
    headers: { 'Content-Type': contentType },
    timeout: 300000,
    maxContentLength: 30 * 1024 * 1024,
    maxBodyLength: 30 * 1024 * 1024,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });

const authHeaders = (token) =>
  token && token !== 'null' && token !== 'undefined' ? { Authorization: `Bearer ${token}` } : {};

/** Direct job-photo upload. `file` should already be compressed by the caller. */
export async function uploadJobPhotoDirect({ apiBase, jobId, token, file, originalName, onProgress, extra = {} }) {
  const headers = authHeaders(token);
  const contentType = file.type || 'image/jpeg';
  const { data: presign } = await axios.post(
    `${apiBase}/jobs/${jobId}/photos/presign-upload`,
    { filename: file.name, content_type: contentType, file_size: file.size },
    { headers },
  );
  await putToS3(presign.upload_url, file, contentType, onProgress);
  let hasThumb = false;
  if (presign.thumb_upload_url) {
    const thumb = await makeThumbnail(file);
    if (thumb) {
      try {
        await putToS3(presign.thumb_upload_url, thumb, 'image/jpeg');
        hasThumb = true;
      } catch {
        hasThumb = false;
      }
    }
  }
  const { data } = await axios.post(
    `${apiBase}/jobs/${jobId}/photos/confirm-upload`,
    {
      photo_id: presign.photo_id,
      filename: presign.filename,
      original_filename: originalName || file.name,
      content_type: contentType,
      has_thumb: hasThumb,
      ...extra,
    },
    { headers },
  );
  return data;
}

/** Direct job-attachment upload (PDF / Word / Excel / ZIP / image). */
export async function uploadJobAttachmentDirect({ apiBase, jobId, token, file, onProgress }) {
  const headers = authHeaders(token);
  const contentType = file.type || 'application/octet-stream';
  const { data: presign } = await axios.post(
    `${apiBase}/jobs/${jobId}/attachments/presign-upload`,
    { filename: file.name, content_type: contentType, file_size: file.size },
    { headers },
  );
  await putToS3(presign.upload_url, file, contentType, onProgress);
  const { data } = await axios.post(
    `${apiBase}/jobs/${jobId}/attachments/confirm-upload`,
    { stored_name: presign.stored_name, original_name: file.name, content_type: contentType },
    { headers },
  );
  return data;
}
